import {
  DatabaseAuto,
  IcommerceUser,
  IdataArrayResponse,
  IfilterProps,
  IvendorBill,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for vendor bill properties with specific generic types
 * @template T - Type for vendor bill (default: IvendorBill<number, IcommerceUser>)
 */
type TvendorBillProps = IvendorBill<number, IcommerceUser>;

/**
 * Represents a vendor bill in the system.
 * This class provides methods to manage and track vendor bills,
 * including creation, retrieval, updating, and filtering of vendor bill records.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TvendorBillProps - The type definition for vendor bill properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link VendorBill} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const vendorBill = await VendorBill.getAll();
 *
 * const vendorBill = await VendorBill.filterAll({
 *
 * });
 *
 * const vendorBill = await VendorBill.bulkDelete({
 *
 * });
 *
 * const vendorBill = await VendorBill.getOne('resourceId');
 *
 * const vendorBill = await VendorBill.remove();
 *
 * ```
 */
export class VendorBill extends DatabaseAuto {
  /** Base API endpoint for vendor bill operations */
  static readonly baseUrl = '/vendor-bill';

  /** Unique reference ID for the vendor bill */
  readonly urId: TvendorBillProps['urId'];

  /** Current status of the vendor bill */
  readonly vendorBillStatus: TvendorBillProps['vendorBillStatus'];

  /** Unique number identifying the vendor bill */
  readonly vendorBillNumber: TvendorBillProps['vendorBillNumber'];

  /** Related invoice information */
  readonly invoice: TvendorBillProps['invoice'];

  /** Items included in the vendor bill */
  readonly items: TvendorBillProps['items'];

  /** Subtotal amount before any additional charges */
  readonly subTotalAmount: TvendorBillProps['subTotalAmount'];

  /** Value of the e-commerce platform's cut/commission */
  readonly ecommerceCutValue: TvendorBillProps['ecommerceCutValue'];

  /** Final amount to be paid out to the vendor */
  readonly payOutAmount: TvendorBillProps['payOutAmount'];

  /** Company associated with the vendor bill */
  readonly company: TvendorBillProps['company'];

  /**
   * Creates a new instance of VendorBill.
   * @param {TvendorBillProps} data - The vendor bill data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TvendorBillProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      }
    });

    super(data);

    this.urId = data.urId;
    this.vendorBillStatus = data.vendorBillStatus;
    this.vendorBillNumber = data.vendorBillNumber;
    this.invoice = data.invoice;
    this.items = data.items;
    this.subTotalAmount = data.subTotalAmount;
    this.ecommerceCutValue = data.ecommerceCutValue;
    this.payOutAmount = data.payOutAmount;
    this.company = data.company;
  }

  /**
   * Retrieves all vendor bills with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: VendorBill[] }>}
   * An object containing the total count and an array of vendor bills
   * @throws {Error} If there's an error fetching the vendor bills
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TvendorBillProps>>(`${VendorBill.baseUrl}?${query}`);

    const vendorBills = await lastValueFrom(observer$);

    return {
      count: vendorBills.count,
      dataArr: vendorBills.data.map(val => new VendorBill(val))
    };
  }

  /**
   * Filters vendor bills based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: VendorBill[] }>}
   * An object containing the total count and an array of filtered vendor bills
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TvendorBillProps>>(
        `${VendorBill.baseUrl}/filter`,
        filter
      );

    const vendorBills = await lastValueFrom(observer$);

    return {
      count: vendorBills.count,
      dataArr: vendorBills.data.map(val => new VendorBill(val))
    };
  }

  /**
   * Retrieves a single vendor bill by its ID.
   * @param {string} resourceId - The unique identifier of the vendor bill to retrieve
   * @returns {Promise<VendorBill>} The requested vendor bill
   * @throws {Error} If the vendor bill is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TvendorBillProps>(`${VendorBill.baseUrl}/${resourceId}`);
    const vendorBill = await lastValueFrom(observer$);

    return new VendorBill(vendorBill);
  }

  /**
   * Updates the status of the current vendor bill.
   * @param {{ vendorBillStatus: TvendorBillProps['vendorBillStatus'] }} payload - The new status for the vendor bill
   * @returns {Promise<VendorBill>} The updated vendor bill
   * @throws {Error} If the update operation fails or the vendor bill doesn't exist
   */
  async update(payload: { vendorBillStatus: TvendorBillProps['vendorBillStatus'] }) {
    const observer$ = CommerceClient.ehttp
      .makePut<TvendorBillProps>(
        `${VendorBill.baseUrl}/${this._id}`,
        payload
      );

    return new VendorBill(await lastValueFrom(observer$));
  }
}
